import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

interface FolderConflictDialogProps {
  open: boolean;
  folderName: string;
  targetFolderName: string;
  onResolve: (resolution: string) => void;
  onCancel: () => void;
}

const FolderConflictDialog: React.FC<FolderConflictDialogProps> = ({
  open,
  folderName,
  targetFolderName,
  onResolve,
  onCancel,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#1a1a1a",
          color: "#fff",
          border: "1px solid #333",
        },
      }}
    >
      <DialogTitle sx={{ color: "hsl(19 101% 51% / 1)" }}>
        Folder already exists
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: "#ccc", mb: 2.5, lineHeight: 1.7 }}>
          {`A folder named '${folderName}' already exists in '${targetFolderName}'. How would you like to handle this?`}
        </Typography>

        <Button
          fullWidth
          variant="outlined"
          color="error"
          onClick={() => onResolve("Override")}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            textTransform: "none",
            mb: 0.5,
            py: 1.25,
            px: 1.5,
            borderColor: "#f44336",
            color: "#ff8a80",
            "&:hover": {
              borderColor: "#ef5350",
              backgroundColor: "rgba(244, 67, 54, 0.08)",
            },
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
            Override & Replace
          </Typography>
          <Typography sx={{ color: "#bbb", fontSize: "0.8rem", mt: 0.5, textAlign: "left" }}>
            Delete the existing folder and all its contents, then move the source folder to this location.
          </Typography>
        </Button>
        <Typography sx={{ color: "#f44336", fontSize: "0.8rem", mb: 1.5, px: 0.5 }}>
          ⚠ This will permanently delete all files in the existing folder. This cannot be undone.
        </Typography>

        <Button
          fullWidth
          variant="outlined"
          onClick={() => onResolve("MergeSingle")}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            textTransform: "none",
            mb: 1.5,
            py: 1.25,
            px: 1.5,
            borderColor: "#ff9800",
            color: "#ffb74d",
            "&:hover": {
              borderColor: "#ffa726",
              backgroundColor: "rgba(255, 152, 0, 0.08)",
            },
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
            Merge into existing
          </Typography>
          <Typography sx={{ color: "#bbb", fontSize: "0.8rem", mt: 0.5, textAlign: "left" }}>
            Move all files from the source folder into the existing folder.
          </Typography>
        </Button>

        <Button
          fullWidth
          variant="outlined"
          onClick={() => onResolve("MergeAsSubfolder")}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            textTransform: "none",
            mb: 1.5,
            py: 1.25,
            px: 1.5,
            borderColor: "#ff9800",
            color: "#ffb74d",
            "&:hover": {
              borderColor: "#ffa726",
              backgroundColor: "rgba(255, 152, 0, 0.08)",
            },
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
            Add as subfolder
          </Typography>
          <Typography sx={{ color: "#bbb", fontSize: "0.8rem", mt: 0.5, textAlign: "left" }}>
            Move the source folder inside the existing folder as a subfolder.
          </Typography>
        </Button>

        <Button
          fullWidth
          variant="outlined"
          onClick={() => onResolve("Rename")}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            textTransform: "none",
            py: 1.25,
            px: 1.5,
            borderColor: "#ff9800",
            color: "#ffb74d",
            "&:hover": {
              borderColor: "#ffa726",
              backgroundColor: "rgba(255, 152, 0, 0.08)",
            },
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
            Rename & move
          </Typography>
          <Typography sx={{ color: "#bbb", fontSize: "0.8rem", mt: 0.5, textAlign: "left" }}>
            Automatically rename the source folder (e.g. FolderName_1) and move it.
          </Typography>
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} sx={{ color: "#fff", textTransform: "none" }}>
          Cancel move
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderConflictDialog;
